import React, { useState } from "react";
import {
  Card,
  CardContent,
  CardFooter,
} from "@/shared/ui/card"
import { Button } from "@/shared/ui/button"
import { toast } from "react-toastify";
import Loader from '@/shared/components/Loader';
import { Label } from "@radix-ui/react-dropdown-menu";
import { Input } from "@/shared/ui/input"
import ReactPasswordChecklist from "react-password-checklist";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/ui/select"
import { useTranslation } from "react-i18next";
import { EyeOffIcon, EyeIcon } from "lucide-react";
import { useAddMemberMutation } from "@/api/rtk/mutationsApi"

export function AddMemberForm({ onSuccess }) {
  const { t } = useTranslation();
  const [addMember] = useAddMemberMutation();

  const initialFormData = {
    name: "",
    role: 'assistant',
    email: "",
    password: "",
  }

  const [formData, setFormData] = useState(initialFormData);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isPassValid, setisPassValid] = useState(false)
  const [isFocused, setIsFocused] = useState(false);

  const validateEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  const disabled = loading || !isPassValid || !formData.name || !formData.email || !validateEmail(formData.email);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleAddMember = async () => {
    try {
      setLoading(true);
      await addMember(formData).unwrap();
      setFormData(initialFormData)
      toast.success(t("profile.members.addSuccess"));
      onSuccess?.();
    } catch (error) {
      console.error("Error creating member:", error.message);
      const message = typeof error === 'string' ? error : error?.data || error?.message;
      switch (message) {
        case 'Invalid organisation Id':
          toast.error('Invalid invite Id');
          break;
        case 'User account already exists':
          toast.error('Email already registered.');
          break;
        default:
          toast.error(t("profile.members.failedToAdd"));
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <CardContent className="px-0 p-0">
      <Card className="rounded-2xl p-2">
        <form className="p-2">
          <div className="grid w-full items-center gap-4 text-left mt-5">
            <div className="flex flex-col space-y-1.5">
              <Label htmlFor="name" className="text-sm font-semibold">{t("common.name")}</Label>
              <Input
                required
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder={t("auth.enterName")}
                autoComplete="off"
                className="text-[#868C98] font-normal "
              />
            </div>
            <div className="flex flex-col space-y-1.5">
              <Label htmlFor="role" className="text-sm font-semibold">{t("profile.members.role")}</Label>
              <Select
                id="role"
                name="role"
                value={formData.role}
                onValueChange={(value) => setFormData({...formData, role: value})}
              >
                <SelectTrigger className="">
                  <SelectValue placeholder={t("profile.members.selectRole")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">{t("profile.members.admin")}</SelectItem>
                  <SelectItem value="assistant">{t("profile.members.assistant")}</SelectItem>
                  <SelectItem value="doctor">{t("profile.members.doctor")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col space-y-1.5">
              <Label htmlFor="email" className="text-sm font-semibold">{t("common.email")}</Label>
              <Input
                required
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                type="email"
                placeholder={t("auth.enterEmail")}
                autoComplete="new-email"
                className="text-[#868C98] font-normal "
              />
            </div>
            <div className="flex flex-col space-y-1.5">
              <Label htmlFor="password" className="text-sm font-semibold">{t("common.password")}</Label>
              <div className='relative'>
                <div className="relative">
                  <Input
                    required
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => setIsFocused(false)}
                    placeholder={t("auth.enterPassword")}
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    className="text-[#868C98] font-normal "
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-3 flex items-center focus:outline-none"
                  >
                    {showPassword ? <EyeOffIcon size={15} strokeWidth={2.5}/> : <EyeIcon size={15} strokeWidth={2.5}/>}
                  </button>
                </div>
                {isFocused && (
                  <Card
                    className="absolute top-full mt-[6px] w-full py-2 px-3 rounded-xl z-50"
                  >
                    <ReactPasswordChecklist
                      className="text-sm font-normal"
                      rules={["minLength", "specialChar", "number", "capital"]}
                      minLength={8}
                      value={formData.password}
                      iconSize={11}
                      invalidColor="#71717a"
                      invalidTextColor="#71717a"
                      onChange={(isValid) => {setisPassValid(isValid)}}
                    />
                  </Card>
                )}
              </div>
            </div>
          </div>
          <CardFooter className="flex justify-center w-full flex-col p-0 mt-5">
            <Button variant="default" className="w-full " onClick={(e) => {
              e.preventDefault()
              handleAddMember()
            }} disabled={disabled}>
              {loading ? 
                <Loader />
              :
                <span>{t("profile.members.createMember")}</span>
              }
            </Button>
          </CardFooter>
        </form>
      </Card>
    </CardContent>
  );
}
